import {
  accordionClasses,
  alertClasses,
  alpha,
  Breakpoint,
  chipClasses,
  createTheme,
  darken,
  emphasize,
  formControlLabelClasses,
  formHelperTextClasses,
  inputAdornmentClasses,
  inputBaseClasses,
  inputLabelClasses,
  PaletteColor,
  paperClasses,
  responsiveFontSizes,
  tableCellClasses,
  tableRowClasses,
  ThemeOptions,
} from '@mui/material';
import { amber } from '@mui/material/colors';
import merge from 'deepmerge';

import { Icon } from '@components/base';
import env from '@config/runtimeConfig';
import { BaseContentType } from '@models/Content';

import { Environment } from './utils/envUtils';

const breakpointValues: { [key in Breakpoint]: number } = {
  xs: 0,
  sm: 680,
  md: 1000,
  lg: 1200,
  xl: 1536,
};

export const BRAND_COLOR = '#EE4238';
const BRAND_SUPPORT_COLOR_YELLOW = '#F7CB81';
const BRAND_SUPPORT_COLOR_MAGENTA = '#801F3D';

const {
  palette: { augmentColor },
} = createTheme();

const createColor = (mainColor: string) => augmentColor({ color: { main: mainColor } });

const darkThemeContentTypeColorMap: Record<BaseContentType, string> = {
  collection: '#72bef0',
  image: '#fff',
  link: '#81c784',
  menu: '#fff59d',
  page: '#f5a7bf',
  title: '#ffaa41',
  unmanaged: '#9177b3',
};

const darkThemePrimaryColorMap: Record<Environment, PaletteColor> = {
  at: createColor(darkThemeContentTypeColorMap.collection),
  local: createColor(darkThemeContentTypeColorMap.unmanaged),
  prod: createColor(BRAND_SUPPORT_COLOR_MAGENTA),
  dev: createColor(darkThemeContentTypeColorMap.link),
};

const darkThemeSecondaryColorMap: Record<Environment, string> = {
  at: emphasize(darkThemePrimaryColorMap.at.main, 0.4),
  local: emphasize(darkThemePrimaryColorMap.local.main, 0.3),
  prod: darkThemeContentTypeColorMap.page,
  dev: emphasize(darkThemePrimaryColorMap.dev.main, 0.4),
};

const lightThemeContentTypeColorMap: Record<BaseContentType, string> = {
  collection: '#2f85bf',
  image: '#000',
  link: '#7FB58A',
  menu: BRAND_SUPPORT_COLOR_YELLOW,
  page: BRAND_SUPPORT_COLOR_MAGENTA,
  title: '#e76f51',
  unmanaged: '#8263ab',
};

const lightThemeEnvColorMap: Record<Environment, PaletteColor> = {
  at: createColor(lightThemeContentTypeColorMap.collection),
  local: createColor(lightThemeContentTypeColorMap.unmanaged),
  prod: createColor(BRAND_SUPPORT_COLOR_MAGENTA),
  dev: createColor(lightThemeContentTypeColorMap.link),
};

const commonThemeOptions: ThemeOptions = {
  breakpoints: { values: breakpointValues },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          [`&.${paperClasses.outlined} + .${accordionClasses.root}.${paperClasses.outlined}:not(.${accordionClasses.expanded})`]:
            {
              borderTop: 0,
              '&:before': {
                display: 'none',
              },
            },
        },
      },
    },
    MuiAccordionSummary: { defaultProps: { expandIcon: <Icon name='expandMore' /> } },
    MuiAutocomplete: { defaultProps: { slotProps: { paper: { elevation: 8 } } } },
    MuiAppBar: { defaultProps: { color: 'default', variant: 'elevation' } },
    MuiBadge: { defaultProps: { color: 'primary' } },
    MuiButton: { defaultProps: { size: 'small' } },
    MuiCheckbox: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: ({ theme }) => ({ padding: theme.spacing() }),
      },
    },
    MuiChip: {
      variants: [
        {
          props: { size: 'tiny' },
          style: ({ theme }) => ({
            height: 18,
            fontSize: theme.typography.caption.fontSize,
            [`.${chipClasses.label}`]: {
              paddingLeft: theme.spacing(0.75),
              paddingRight: theme.spacing(0.75),
            },
          }),
        },
        {
          props: { variant: 'contrast' },
          style: ({ theme }) => ({
            background: theme.palette.text.primary,
            color: theme.palette.getContrastText(theme.palette.text.primary),
            '&:hover': {
              background: alpha(theme.palette.text.primary, 1 - theme.palette.action.hoverOpacity),
            },
            '&:focus': {
              background: alpha(theme.palette.text.primary, 1 - theme.palette.action.focusOpacity),
            },
          }),
        },
        {
          props: { color: 'warning', variant: 'filled' },
          style: ({ theme }) => ({
            border: `thin solid ${theme.palette.warning.dark}`,
          }),
        },
      ],
      styleOverrides: {
        root: ({ theme }) => ({
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.text.primary}`,
            outlineOffset: '-2px',
          },
        }),
        label: { lineHeight: 'normal' },
        outlined: ({ theme }) => ({
          [`&.${chipClasses.colorSuccess}`]: {
            borderColor: alpha(theme.palette.success.main, 0.2),
            background: alpha(theme.palette.success.main, 0.05),
          },
          [`&.${chipClasses.colorWarning}`]: {
            borderColor: alpha(theme.palette.warning.main, 0.2),
            background: alpha(theme.palette.warning.main, 0.05),
          },
          [`&.${chipClasses.colorError}`]: {
            borderColor: alpha(theme.palette.error.main, 0.2),
            background: alpha(theme.palette.error.main, 0.05),
          },
          [`&.${chipClasses.colorInfo}`]: {
            borderColor: alpha(theme.palette.info.main, 0.2),
            background: alpha(theme.palette.info.main, 0.05),
          },
          [`&.${chipClasses.colorPrimary}`]: {
            borderColor: alpha(theme.palette.primary.main, 0.2),
            background: alpha(theme.palette.primary.main, 0.05),
          },
          [`&.${chipClasses.colorSecondary}`]: {
            borderColor: alpha(theme.palette.secondary.main, 0.2),
            background: alpha(theme.palette.secondary.main, 0.05),
          },
        }),
      },
    },
    MuiCircularProgress: { defaultProps: { color: 'inherit' } },
    MuiCollapse: { defaultProps: { unmountOnExit: true } },
    MuiDialog: { defaultProps: { scroll: 'body' } },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          overflow: 'visible',
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        }),
      },
    },
    MuiDialogTitle: {
      defaultProps: { variant: 'h2' },
      styleOverrides: {
        root: ({ theme }) => ({ padding: theme.spacing(1.5, 2) }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          background: theme.palette.background.default,
        }),
      },
    },
    MuiFab: {
      defaultProps: { size: 'medium', color: 'primary' },
      styleOverrides: {
        root: { zIndex: 'auto', boxShadow: 'none' },
        sizeMedium: { height: 40, width: 40 },
        sizeSmall: { height: 24, width: 24, minHeight: 24, minWidth: 24 },
      },
    },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.${formControlLabelClasses.disabled}`]: {
            opacity: theme.palette.action.disabledOpacity,
          },
        }),
        labelPlacementStart: { marginLeft: 0 },
      },
    },
    MuiFormLabel: { styleOverrides: { root: { zIndex: '2 !important' } } },
    MuiIconButton: {
      variants: [
        {
          props: { size: 'tiny' },
          style: { padding: 3, fontSize: '1.125rem' },
        },
      ],
      defaultProps: { size: 'small' },
      styleOverrides: { sizeLarge: { width: 44, height: 44 } },
    },
    MuiIcon: { defaultProps: { fontSize: 'small' } },
    MuiLink: { defaultProps: { color: 'text.primary' } },
    MuiList: { styleOverrides: { dense: { paddingTop: 0, paddingBottom: 0 } } },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:not(.no-hover):hover': { background: theme.palette.action.hover },
        }),
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 32 } } },
    MuiListItemText: { styleOverrides: { inset: { paddingLeft: 32 } } },
    MuiMenu: { styleOverrides: { paper: { maxHeight: 600, minWidth: 180 } } },
    MuiMenuItem: { defaultProps: { dense: true } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiTextField: {
      defaultProps: { size: 'small' },
      variants: [
        {
          props: { size: 'tiny' },
          style: ({ theme }) => ({
            [`.${inputBaseClasses.input}`]: {
              fontSize: theme.typography.body2.fontSize,
              padding: '4px 6px',
            },
            [`.${formHelperTextClasses.root}`]: { marginLeft: 6 },
          }),
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '*': { zIndex: 1 },
          fieldset: { background: alpha(theme.palette.background.default, 0.5), zIndex: 0 },
        }),
        adornedStart: {
          paddingLeft: 6,
          [`.${inputAdornmentClasses.positionStart}:has(button)`]: {
            marginRight: 0,
            '+ input': { paddingLeft: 6 },
          },
        },
        adornedEnd: { paddingRight: 12 },
      },
    },
    MuiSwitch: { defaultProps: { size: 'small' } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          [`&.${tableCellClasses.paddingCheckbox}`]: { padding: 6 },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&:last-of-type ${tableCellClasses.root}`]: { borderBottom: 0 },
          [`&.${tableRowClasses.selected}`]: {
            background: alpha(theme.palette.info.main, theme.palette.action.selectedOpacity),
            '&:hover': {
              background: alpha(theme.palette.info.main, theme.palette.action.hoverOpacity),
            },
          },
        }),
      },
    },
    MuiTablePagination: {
      defaultProps: {
        slotProps: {
          actions: { previousButton: { size: 'small' }, nextButton: { size: 'small' } },
        },
      },
      styleOverrides: {
        actions: ({ theme }) => ({ marginLeft: theme.spacing() }),
        displayedRows: { margin: 0 },
      },
    },
    MuiToggleButton: { styleOverrides: { sizeSmall: { height: 40 } } },
    MuiToggleButtonGroup: { defaultProps: { size: 'small' } },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        disableInteractive: true,
        enterDelay: 500,
        enterNextDelay: 500,
      },
    },
  },
  shape: { borderRadius: 4 },
  typography: {
    fontFamily: 'IBM Plex Sans',
    h1: { fontWeight: 'semibold', fontSize: '1.8rem', lineHeight: 1.2 },
    h2: { fontWeight: 'semibold', fontSize: '1.4rem', lineHeight: 1.2 },
    h3: { fontWeight: 'semibold', fontSize: '1.2rem', lineHeight: 1.2 },
    h4: { fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 },
    h5: { fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 },
    h6: { fontWeight: 'bold', fontSize: '0.8rem', lineHeight: 1.2 },
    overline: { fontWeight: 'bold', letterSpacing: '0.1ch', lineHeight: 1.5 },
    'Altibox-Light': {
      fontFamily: 'Altibox-Light',
      fontSize: '2.125rem',
      lineHeight: 1,
      whiteSpace: 'break-spaces',
    },
    'Altibox-STB': {
      fontFamily: 'Altibox-STB',
      fontSize: '2.125rem',
      lineHeight: 1,
      whiteSpace: 'break-spaces',
    },
  },
  zIndex: { drawer: 1300 },
};

export const createAtsTheme = (options: ThemeOptions) =>
  responsiveFontSizes(createTheme(merge(commonThemeOptions, options)));

const getDarkThemeOptions = (): ThemeOptions => {
  const isProd = env === 'prod';

  return {
    palette: {
      action: {
        active: 'rgba(255, 255, 255, 0.8)',
      },
      custom: {
        content: darkThemeContentTypeColorMap,
        env: darkThemePrimaryColorMap,
      },
      mode: 'dark',
      info: { main: '#66C4EB' },
      error: { main: '#FD6340' },
      success: { main: darkThemeContentTypeColorMap.link },
      warning: { main: '#efb54d' },
      primary: darkThemePrimaryColorMap[env],
      secondary: {
        main: darkThemeSecondaryColorMap[env],
      },
      background: {
        default: '#0a0a0a',
        paper: '#1e232c',
      },
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: {
            [`.${alertClasses.message}`]: { flex: 1 },
          },
          filledError: ({ theme }) => ({
            color: theme.palette.getContrastText(theme.palette.error.dark),
          }),
          filledSuccess: ({ theme }) => ({
            color: theme.palette.getContrastText(theme.palette.success.dark),
          }),
          filledWarning: ({ theme }) => ({
            color: theme.palette.getContrastText(theme.palette.warning.dark),
          }),
          filledInfo: ({ theme }) => ({
            color: theme.palette.getContrastText(theme.palette.info.dark),
          }),

          standardError: ({ theme }) => ({
            border: `thin solid ${alpha(theme.palette.error.dark, 0.2)}`,
          }),
          standardSuccess: ({ theme }) => ({
            border: `thin solid ${alpha(theme.palette.success.dark, 0.2)}`,
          }),
          standardWarning: ({ theme }) => ({
            border: `thin solid ${alpha(theme.palette.warning.dark, 0.2)}`,
          }),
          standardInfo: ({ theme }) => ({
            border: `thin solid ${alpha(theme.palette.info.dark, 0.2)}`,
          }),

          outlinedError: ({ theme }) => ({
            borderColor: alpha(theme.palette.error.dark, 0.5),
          }),
          outlinedSuccess: ({ theme }) => ({
            borderColor: alpha(theme.palette.success.dark, 0.5),
          }),
          outlinedWarning: ({ theme }) => ({
            borderColor: alpha(theme.palette.warning.dark, 0.5),
          }),
          outlinedInfo: ({ theme }) => ({
            borderColor: alpha(theme.palette.info.dark, 0.5),
          }),
          message: { fontWeight: 'normal' },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { background: alpha('#fff', 0.2) },
          invisible: { background: 'transparent' },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'text', color: 'primary' },
            style: ({ theme }) => ({
              color: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: alpha(
                  theme.palette.secondary.main,
                  theme.palette.action.hoverOpacity
                ),
              },
            }),
          },
        ],
      },
      ...(isProd
        ? {
            MuiCheckbox: { defaultProps: { color: 'secondary' } },
            MuiFormLabel: { defaultProps: { color: 'secondary' } },
            MuiRadio: { defaultProps: { color: 'secondary' } },
            MuiSwitch: { defaultProps: { color: 'secondary', size: 'small' } },
            MuiTextField: { defaultProps: { color: 'secondary' } },
          }
        : {}),
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            background: theme.palette.background.default,
            backgroundImage: 'none',
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`&.${formHelperTextClasses.error}`]: {
              color: theme.palette.error.light,
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          asterisk: ({ theme }) => ({
            [`&.${inputLabelClasses.error}`]: {
              color: theme.palette.error.light,
            },
          }),
          root: ({ theme }) => ({
            [`&.${inputLabelClasses.error}`]: {
              color: theme.palette.error.light,
            },
          }),
        },
      },
      MuiPaper: { defaultProps: { elevation: 0 } },
      MuiPopover: { defaultProps: { elevation: 8 } },
      MuiTabs: { defaultProps: { textColor: 'inherit' } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            background: theme.palette.common.white,
            color: theme.palette.getContrastText(theme.palette.common.white),
          }),
          arrow: ({ theme }) => ({ color: theme.palette.common.white }),
        },
      },
    },
  };
};

export const darkTheme = createAtsTheme(getDarkThemeOptions());

const getLightThemeOptions = (): ThemeOptions => ({
  palette: {
    custom: {
      content: lightThemeContentTypeColorMap,
      env: lightThemeEnvColorMap,
    },
    primary: lightThemeEnvColorMap[env],
    secondary: { main: darken(lightThemeEnvColorMap[env].main, 0.5) },
    warning: { main: amber.A400 },
    background: { default: 'rgb(245, 245, 245)' },
  },
  components: {
    ...commonThemeOptions.components,
    MuiButton: {
      styleOverrides: {
        textPrimary: ({ theme, ownerState }) => ({
          ...(!ownerState.color
            ? {
                color: theme.palette.secondary.main,
                '&:hover': {
                  backgroundColor: alpha(
                    theme.palette.secondary.main,
                    theme.palette.action.hoverOpacity
                  ),
                },
              }
            : {}),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        clickable: ({ theme }) => ({
          [`&.${chipClasses.focusVisible}`]: { outlineColor: theme.palette.text.primary },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({ fieldset: { background: theme.palette.background.default } }),
        input: { zIndex: 1 },
      },
    },
    MuiAccordion: { defaultProps: { variant: 'elevation' } },
    MuiPaper: { defaultProps: { variant: 'outlined' } },
    MuiTooltip: {
      styleOverrides: {
        tooltipArrow: ({ theme }) => ({
          background: theme.palette.common.black,
          color: theme.palette.getContrastText(theme.palette.common.black),
        }),
        arrow: ({ theme }) => ({ color: theme.palette.common.black }),
      },
    },
  },
});

export const lightTheme = createAtsTheme(getLightThemeOptions());
